import React, { ReactElement, useEffect, useState } from "react";

import _ from "lodash";
import { ConnectedProps, connect, useDispatch } from "react-redux";

import NoteIcon from "@mui/icons-material/Book";
import UploadCloudIcon from "@mui/icons-material/CloudUpload";
import CompleteIcon from "@mui/icons-material/CheckCircle";
import IncompleteIcon from "@mui/icons-material/RadioButtonUnchecked";
import NotApplicableIcon from "@mui/icons-material/RemoveCircle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import { DataGrid, GridColDef, GridRenderCellParams, GridSelectionModel } from "@mui/x-data-grid";

import { nonce } from "App/App";
import { AppDispatch, RootState } from "App/Store";

import { getFullDocumentPackageTypeDescription } from "Common/Utilities";
import { formatVaultUploadDate } from "Common/dateUtilities";

import ErrorOverlay from "Components/DataGridCustomComponents/ErrorOverlay";
import LoadingOverlay from "Components/DataGridCustomComponents/LoadingOverlay";
import { ExternalLinkIcon, InkSignedDocumentIcon, PartialIcon, PenIcon, UnknownIcon } from "Components/Icons";
import { ModalPopupAlert } from "Components/Notifications/ModalPopupAlert";
import ScrollContainer from "Components/ScrollContainer";
import Tooltip from "Components/Tooltip";

import { WorkflowType } from "Types/EVaultAppTypes";

import { profileSelector } from "Features/Profile/ProfileSlice";
import * as tabActions from "Features/Tabs/Tabs";
import { ITabIdentifier } from "Features/Tabs/Tabs";

import { fetchDocuments } from "../Documents/DocumentsSlice";
import { IVaultViewAllLoansDocumentDetailData } from "../VaultInterfaces";
import { DocumentDownloadProperties, getDocumentPrintUrl } from "../documentService";
import * as documentService from "../documentService";
import { datagridColumns } from "../shared";
import { AddDocumentModal } from "../Mers/AddDocument/AddDocumentModal";
import {
  DocListMersEligibilityContext,
  getAddDocumentEligibility,
} from "../Mers/AddDocument/getAddDocumentEligibility";
import { getReverseDocumentMenuState } from "../Mers/ReverseDocument/getReverseDocumentMenuState";
import { ReverseDocumentModal } from "../Mers/ReverseDocument/ReverseDocumentModal";
import DocumentContextMenu from "./DocumentContextMenu";
import DocumentListContextMenu, { DocumentOptions } from "./ListContextMenu";
import documentListService from "./documentListService";

export interface IDocumentListProps {
  alternatePackageType: string;
  isLoading?: boolean;
  loanNumber: string;
  mersEligibility?: DocListMersEligibilityContext;
  packageId: string;
  packageType: string;
  partyName: string;
  tabIdentifier: ITabIdentifier;
  workflowType?: WorkflowType;
}

export enum DocumentAction {
  Print,
  Download,
}

const mapStateToProps = (state: RootState, props: IDocumentListProps) => {
  const packageDocuments = state.documents.documentsByPackageId[props.packageId];
  let documents: IVaultViewAllLoansDocumentDetailData[] = [];
  if (packageDocuments) {
    documents = packageDocuments.documents;
  }
  return {
    addDocumentEnabled: profileSelector(state).settings.mers.addDocumentEnabled,
    canDownload: documentService.canUserDownload(state),
    displayAltPackage: profileSelector(state).settings.application.displayAltPackage,
    documents: documents,
    error: packageDocuments?.error,
    preSelectedMode: documentService.preSelectedMode(state),
  };
};

const connector = connect(mapStateToProps);
type TypesFromRedux = ConnectedProps<typeof connector>;

type Props = TypesFromRedux & IDocumentListProps;

export function DocumentList(props: Props): ReactElement | null {
  const {
    addDocumentEnabled,
    alternatePackageType,
    canDownload,

    displayAltPackage,
    documents,
    error,
    isLoading,
    loanNumber,
    mersEligibility,
    packageId,
    packageType,
    partyName,
    preSelectedMode,
    workflowType,
  } = props;

  const dispatch = useDispatch<AppDispatch>();
  const [alertNoDocumentsSelected, setAlertNoDocumentsSelected] = useState<boolean>(false);
  const [selected, setSelected] = useState<GridSelectionModel>([]);
  const [isAddDocumentOpen, setIsAddDocumentOpen] = useState<boolean>(false);
  const [reverseDocumentTarget, setReverseDocumentTarget] = useState<{
    documentId: number;
    documentName: string;
    mersDocumentType: string | null;
  } | null>(null);
  const addDocumentEligibility = getAddDocumentEligibility(addDocumentEnabled ? mersEligibility : undefined);

  useEffect(() => {
    let initiallySelectedRows: GridSelectionModel;
    switch (preSelectedMode) {
      case "AllDocs":
        initiallySelectedRows = _.map(documents, (item) => item.id);
        break;
      case "eSignDocs":
        initiallySelectedRows = _.filter(
          documents,
          (item: IVaultViewAllLoansDocumentDetailData) => !item.isInkSign
        ).map((item) => item.id);
        break;
      case "InkSignDocs":
        initiallySelectedRows = _.filter(documents, (item: IVaultViewAllLoansDocumentDetailData) => item.isInkSign).map(
          (item) => item.id
        );
        break;
      default:
        initiallySelectedRows = [];
        break;
    }
    setSelected(initiallySelectedRows);
  }, [documents, preSelectedMode]);

  function renderColumns(): GridColDef[] {
    const auditLogDisabled = workflowType === "MERS";
    const auditLogLabel = "Audit Log";
    const oneOrMoreDocuments = Boolean(documents?.length);
    const fullPackageTypeDescription = getFullDocumentPackageTypeDescription(
      displayAltPackage,
      packageType,
      alternatePackageType
    );

    const renderAuditLogLink = () => {
      if (canDownload) {
        return (
          <>
            <Divider orientation="vertical" variant="middle" flexItem sx={{ alignSelf: "center", height: 20, mx: 1 }} />
            <span>
              {oneOrMoreDocuments ? (
                <Link
                  sx={{
                    alignItems: "center",
                    color: (theme) => (auditLogDisabled ? theme.palette.grey[500] : "initial"),
                    cursor: auditLogDisabled ? "default" : "pointer",
                    display: "flex",
                  }}
                  underline={auditLogDisabled ? "none" : "hover"}
                  href={
                    auditLogDisabled
                      ? undefined
                      : documentService.getAuditLogLink({
                          id: packageId,
                          loanNumber: loanNumber,
                          packageId: packageId,
                        })
                  }
                  target="_blank"
                  rel="noopener"
                >
                  {auditLogLabel}
                  <ExternalLinkIcon sx={{ fontSize: 14, ml: 0.5 }} />
                </Link>
              ) : (
                auditLogLabel
              )}
            </span>
          </>
        );
      }
    };

    return [
      {
        field: "display",
        flex: 1,
        minWidth: 500,
        renderCell: (params: GridRenderCellParams) => (
          <Grid container item alignItems="center" justifyContent="space-between">
            {canDownload ? (
              <a href={params.row.printLink} target="_blank" onClick={handleAnchorClick} rel="noreferrer">
                {params.row.name}
              </a>
            ) : (
              <span>{params.row.name}</span>
            )}
            {params.row.hasAssociatedSMARTDoc && canDownload && (
              <Tooltip title="eNote">
                <NoteIcon />
              </Tooltip>
            )}
            {params.row.isInkSign && <InkSignedDocumentIcon fontSize="large" sx={{ pl: 1.5 }} />}
          </Grid>
        ),
        renderHeader: function renderDisplayHeader() {
          return isLoading ? (
            <Box sx={{ alignItems: "center", display: "flex" }}>Loading documents...</Box>
          ) : (
            <Box sx={{ alignItems: "center", display: "flex", width: "100%" }}>
              <Box sx={{ alignItems: "center", display: "flex", flexShrink: 0, minWidth: 0 }}>
                {`LOAN #: ${loanNumber}${fullPackageTypeDescription && `, ${fullPackageTypeDescription} Package`}`}
                {renderAuditLogLink()}
              </Box>
              {addDocumentEnabled && (
                <Tooltip
                  disableHoverListener={addDocumentEligibility.eligible}
                  title={addDocumentEligibility.eligible ? "" : addDocumentEligibility.reason ?? ""}
                >
                  <Box sx={{ flexShrink: 0, ml: "auto" }}>
                    <Button
                      disabled={!addDocumentEligibility.eligible}
                      onClick={() => {
                        if (addDocumentEligibility.eligible) {
                          setIsAddDocumentOpen(true);
                        }
                      }}
                      size="small"
                      startIcon={<UploadCloudIcon />}
                      variant="outlined"
                    >
                      Add Document
                    </Button>
                  </Box>
                </Tooltip>
              )}
            </Box>
          );
        },
        sortable: false,
      },
      {
        align: "right",
        field: "signStatus",
        headerAlign: "center",
        minWidth: 100,
        renderCell: (params: GridRenderCellParams) => getSignStatus(params.row as IVaultViewAllLoansDocumentDetailData),
        renderHeader: function renderSignStatusHeader() {
          return (
            <Tooltip title="eSign Status">
              <Grid container item alignItems="center" justifyContent="center">
                <PenIcon sx={{ color: (theme) => theme.palette.grey[700] }} />
              </Grid>
            </Tooltip>
          );
        },
        sortable: false,
      },
      ...(addDocumentEnabled
        ? [
            {
              field: "mersDocumentType",
              headerName: "MERS Document Type",
              renderCell: (params: GridRenderCellParams) => params.value ?? "---",
              sortable: false,
              width: 180,
            } as GridColDef,
            {
              field: "mersStatus",
              headerName: "Status",
              renderCell: (params: GridRenderCellParams) =>
                documentListService.formatMersStatusLabel(params.value as string | null),
              sortable: false,
              width: 160,
            } as GridColDef,
            {
              field: "uploadedBy",
              headerName: "Uploaded By",
              renderCell: (params: GridRenderCellParams) => params.value ?? "---",
              sortable: false,
              width: 140,
            } as GridColDef,
            {
              field: "uploadedDate",
              headerName: "Date",
              renderCell: (params: GridRenderCellParams) => formatVaultUploadDate(params.value as string | null),
              sortable: false,
              width: 120,
            } as GridColDef,
          ]
        : []),
      {
        ...datagridColumns.contextMenu,
        renderCell: (params: GridRenderCellParams) => {
          const document = params.row as IVaultViewAllLoansDocumentDetailData;
          const reverseMenu = getReverseDocumentMenuState(document, mersEligibility);

          return (
            <DocumentContextMenu
              showSmartDoc={params.row.hasAssociatedSMARTDoc && canDownload}
              packageId={packageId}
              smartDocId={params.row.id}
              onDownload={(id) => handleDocumentActionById(id, DocumentAction.Download)}
              onPrint={(id) => handleDocumentActionById(id, DocumentAction.Print)}
              onReverseDocument={(id) => {
                setReverseDocumentTarget({
                  documentId: id,
                  documentName: document.name,
                  mersDocumentType: document.mersDocumentType,
                });
              }}
              reverseDocumentDisabled={reverseMenu.disabled}
              reverseDocumentDisabledReason={reverseMenu.disabledReason}
              showReverseDocument={addDocumentEnabled && reverseMenu.show}
            />
          );
        },
        renderHeader: function renderContextMenuHeader() {
          return (
            <DocumentListContextMenu
              auditLogData={{
                id: packageId,
                loanNumber,
                packageId,
              }}
              checkedRows={selected}
              documents={documents}
              onDownload={(option) => handleDocumentActionByOption(option, DocumentAction.Download)}
              onPrint={(option) => handleDocumentActionByOption(option, DocumentAction.Print)}
              workflowType={workflowType}
            />
          );
        },
      },
    ];
  }

  function handleAnchorClick(event: any): void {
    event.stopPropagation();
  }

  const onSelectionModelChange = (selected: GridSelectionModel) => {
    setSelected(selected);
  };

  function handleDocumentActionByOption(option: DocumentOptions, action: DocumentAction) {
    const documentList: GridSelectionModel = documentListService.getDocumentIdsForOption(documents, option, selected);
    const documentProperties = {
      documentList,
      loanNumber,
      packageId,
      partyName,
    };
    if (action === DocumentAction.Print) {
      documentService.openDocumentPrint(documentProperties);
    } else if (action === DocumentAction.Download) {
      documentService.openDocumentDownload(documentProperties);
    }
  }

  function handleDocumentActionById(documentId: number, action: DocumentAction) {
    const documentList: number[] = [documentId];
    const documentProperties = {
      documentList,
      loanNumber,
      packageId,
      partyName,
    };
    if (action === DocumentAction.Print) {
      documentService.openDocumentPrint(documentProperties);
    } else if (action === DocumentAction.Download) {
      documentService.openDocumentDownload(documentProperties);
    }
  }

  const noDocsSelectedErrorModal = (
    <ModalPopupAlert
      title="Select docs"
      content="To print or download, you must select at least one doc."
      onClose={() => {
        setAlertNoDocumentsSelected(false);
      }}
      open={alertNoDocumentsSelected}
      size="sm"
    />
  );

  const rows = _.map(documents, (item) => {
    const isMersWorkflow = workflowType === "MERS";
    const properties: DocumentDownloadProperties = {
      documentList: [item.id],
      loanNumber,
      packageId,
      partyName,
    };
    let printLink = getDocumentPrintUrl(properties);
    if (isMersWorkflow && item.hasAssociatedSMARTDoc) {
      printLink = `/api/packages/${packageId}/documents/${item.id}/smart-doc-content`;
    }

    return {
      ...item,
      checkDisabled: item.mediaType !== "PDF",
      printLink,
    } as IVaultViewAllLoansDocumentDetailData;
  });

  function getSignStatus(result: IVaultViewAllLoansDocumentDetailData) {
    let signStatus;
    switch (result.signatureStatus.toLowerCase()) {
      case "fullysigned":
        signStatus = (
          <Tooltip title="Complete">
            <CompleteIcon fontSize="small" />
          </Tooltip>
        );
        break;

      case "notsignable":
        signStatus = (
          <Tooltip title="Not eSignable">
            <NotApplicableIcon fontSize="small" />
          </Tooltip>
        );
        break;

      case "partiallysigned":
        signStatus = (
          <Tooltip title="Partial">
            <PartialIcon fontSize="small" />
          </Tooltip>
        );
        break;

      case "unsigned":
        signStatus = (
          <Tooltip title="None complete">
            <IncompleteIcon fontSize="small" />
          </Tooltip>
        );
        break;

      default:
        signStatus = (
          <Tooltip title={result.signatureStatus.toLowerCase()}>
            <UnknownIcon />
          </Tooltip>
        );
        break;
    }
    return (
      <Grid container item alignItems="center" justifyContent="center">
        {signStatus}
      </Grid>
    );
  }

  function handleRetry() {
    dispatch(fetchDocuments(packageId));
  }

  function handleGoBack() {
    dispatch(tabActions.removeTab(props.tabIdentifier));
  }

  return (
    <ScrollContainer>
      {noDocsSelectedErrorModal}
      <DataGrid
        autoHeight
        checkboxSelection
        columnBuffer={0}
        columns={renderColumns()}
        components={{
          ErrorOverlay: ErrorOverlay,
          LoadingOverlay: LoadingOverlay,
        }}
        componentsProps={{
          errorOverlay: {
            error: "An error occurred while loading the doc list.",
            onGoBack: handleGoBack,
            onRetry: handleRetry,
          },
        }}
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        disableSelectionOnClick
        error={error}
        hideFooter
        loading={isLoading}
        localeText={{
          noRowsLabel: "No results found",
        }}
        nonce={nonce}
        onSelectionModelChange={onSelectionModelChange}
        selectionModel={selected}
        rows={rows}
        scrollbarSize={0}
        sx={
          addDocumentEnabled
            ? {
                "& .MuiDataGrid-columnHeader[data-field=\"display\"] .MuiDataGrid-columnHeaderTitleContainerContent": {
                  flex: 1,
                  minWidth: 0,
                  width: "100%",
                },
              }
            : undefined
        }
      />
      {addDocumentEnabled && (
        <AddDocumentModal
          existingMersDocuments={documents.map((document) => ({
            mersDocumentType: document.mersDocumentType,
            mersSignatureValue: document.mersSignatureValue,
            mersStatus: document.mersStatus,
            name: document.name,
          }))}
          onClose={() => setIsAddDocumentOpen(false)}
          onSuccess={() => {
            setIsAddDocumentOpen(false);
            dispatch(fetchDocuments(packageId));
          }}
          open={isAddDocumentOpen}
          packageId={packageId}
        />
      )}
      {addDocumentEnabled && reverseDocumentTarget && (
        <ReverseDocumentModal
          documentId={reverseDocumentTarget.documentId}
          documentName={reverseDocumentTarget.documentName}
          mersDocumentType={reverseDocumentTarget.mersDocumentType}
          onClose={() => setReverseDocumentTarget(null)}
          onSuccess={() => {
            setReverseDocumentTarget(null);
            dispatch(fetchDocuments(packageId));
          }}
          open={Boolean(reverseDocumentTarget)}
          packageId={packageId}
        />
      )}
    </ScrollContainer>
  );
}

export default connector(DocumentList);
